import React from 'react';
import { motion } from 'framer-motion';

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold tracking-[0.01em] rounded-full transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden';

  const variants = {
    primary: 'bg-gradient-to-r from-pragyavan-gold via-accent-gold to-pragyavan-gold text-pragyavan-brown border border-pragyavan-gold/40 shadow-[0_12px_24px_-12px_rgba(244,208,63,0.65)] hover:-translate-y-1 hover:shadow-[0_20px_40px_-16px_rgba(244,208,63,0.7)] hover:border-pragyavan-gold/60 focus:ring-pragyavan-gold before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/0 before:via-white/25 before:to-white/0 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700',
    secondary: 'bg-white/70 backdrop-blur-sm border border-pragyavan-brown/20 text-pragyavan-brown hover:bg-pragyavan-brown hover:text-soft-white hover:border-pragyavan-brown hover:shadow-lg focus:ring-pragyavan-brown',
    outline: 'bg-transparent border-2 border-pragyavan-gold/50 text-pragyavan-brown hover:bg-pragyavan-gold/15 hover:border-pragyavan-gold hover:shadow-[0_8px_20px_-8px_rgba(244,208,63,0.4)] focus:ring-pragyavan-gold',
    ghost: 'bg-transparent text-pragyavan-brown hover:bg-pragyavan-cream/80 hover:shadow-sm focus:ring-pragyavan-gold',
    danger: 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/25 focus:ring-red-500',
    glass: 'bg-white/40 backdrop-blur-md border border-white/40 text-pragyavan-brown hover:bg-white/70 hover:shadow-glass hover:border-white/60 focus:ring-pragyavan-gold'
  };

  const sizes = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg'
  };

  const buttonClasses = `
    ${baseClasses}
    ${variants[variant]}
    ${sizes[size]}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim();

  return (
    <motion.button
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      aria-busy={loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}

      {Icon && iconPosition === 'left' && !loading && (
        <Icon className="w-5 h-5 mr-2" />
      )}

      {children}

      {Icon && iconPosition === 'right' && !loading && (
        <Icon className="w-5 h-5 ml-2" />
      )}
    </motion.button>
  );
};

export default Button;
